import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getisCompletedAssessment, getGroupsByUserId } from "../axios/axiosFunctions";
import { Alert, Box, Button, CircularProgress, Snackbar, Typography } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DownloadIcon from "@mui/icons-material/Download";
import { downloadSelfAssessmentPDF } from "../utils/downloadSelfAssessment";
import { isTimFamilySlug, categoryForSlug } from "../utils/timFamily";

const SELF_SLUGS = new Set(["mim_self_assessment", "lim_self_assessment"]);

const MyAssessmentCardProfile = ({ assessment, userName = "" }) => {
  const { userId } = useParams();
  const [completed, setCompleted] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState("");
  // TIM-family team assessments: what the user sees is the TEAM report (not an
  // individual report), and a member can belong to more than one team — so we
  // list each of their teams and link to that team's report.
  const isTim = isTimFamilySlug(assessment.slug);
  const [timTeams, setTimTeams] = useState([]);

  useEffect(() => {
    if (assessment.is360) return;
    const checkIfComplete = async () => {
      const isComplete = await getisCompletedAssessment(assessment._id, userId);
      if (isComplete.valid) {
        setCompleted(true);
      }
    };
    checkIfComplete();
  }, [assessment]);

  useEffect(() => {
    if (!isTim || !userId) return;
    const loadTeams = async () => {
      const res = await getGroupsByUserId(userId);
      const category = categoryForSlug(assessment.slug);
      setTimTeams((res?.groups || []).filter((g) => g.category === category));
    };
    loadTeams();
  }, [isTim, userId, assessment.slug]);

  const handleDownload = async () => {
    setDownloading(true);
    setDownloadError("");
    try {
      await downloadSelfAssessmentPDF({
        userId,
        slug: assessment.slug,
        name: userName,
      });
    } catch (err) {
      console.log("Self-assessment PDF download failed:", err);
      setDownloadError("Could not generate the PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const canDownload = completed && SELF_SLUGS.has(assessment.slug);

  return (
    <div className="assessmentCard">
      <div
        style={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0 }}
      >
        <div className="assessmentCardIcon">
          <img
            className="assesmentMainImage"
            alt="Assessment Logo"
            src={assessment.mainImage}
          />
        </div>
        <Typography
          component={"div"}
          variant="h5"
          fontWeight={600}
          align="center"
          color="secondary"
          sx={{
            marginLeft: "30px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
            minWidth: 0,
            marginRight: "1rem",
          }}
          dangerouslySetInnerHTML={{ __html: `${assessment.name}` }}
        ></Typography>
      </div>
      {!assessment.is360 && (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          {isTim ? (
            timTeams.length > 0 ? (
              timTeams.map((team) => (
                <Button
                  key={team._id}
                  component={Link}
                  to={`/${assessment.slug}/${team._id}/report`}
                  className="assessmentCardButton"
                  variant="contained"
                  startIcon={<ArrowForwardIcon className="invertIcon" />}
                >
                  View {team.name} report
                </Button>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">No team yet</Typography>
            )
          ) : (
            <>
              {completed && (
                <Button
                  component={Link}
                  to={`/profile/${assessment.slug}/${userId}`}
                  className="assessmentCardButton"
                  variant="contained"
                  startIcon={<ArrowForwardIcon className="invertIcon" />}
                >
                  View Results
                </Button>
              )}
              {canDownload && (
                <Button
                  onClick={handleDownload}
                  variant="outlined"
                  color="secondary"
                  disabled={downloading}
                  startIcon={
                    downloading ? (
                      <CircularProgress size={16} />
                    ) : (
                      <DownloadIcon />
                    )
                  }
                  sx={{ textWrap: "nowrap" }}
                >
                  {downloading ? "Generating…" : "Download PDF"}
                </Button>
              )}
              {!completed && (
                <Button
                  className="assessmentCardButton borderButtonGreen"
                  variant="contained"
                  sx={{ marginRight: "30px", textWrap: "nowrap" }}
                >
                  In process...
                </Button>
              )}
            </>
          )}
        </Box>
      )}
      <Snackbar
        open={!!downloadError}
        autoHideDuration={5000}
        onClose={() => setDownloadError("")}
      >
        <Alert severity="error" onClose={() => setDownloadError("")}>
          {downloadError}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default MyAssessmentCardProfile;
